import { createContext, useContext } from 'react';
import type { Role } from './roles';
import type { Me, MfaSetup, SessionState } from './types';

export interface SessionContextValue {
  session: SessionState;
  me: Me | null;
  /** Password step. Resolves 'mfa' when a second factor (or its setup) is needed. */
  signIn: (email: string, password: string) => Promise<Me | 'mfa'>;
  /** First sign-in of a role that must use two-factor: the secret to scan. */
  startMfaSetup: () => Promise<MfaSetup>;
  verifyMfa: (
    proof: { code: string } | { recoveryCode: string },
  ) => Promise<Me>;
  cancelMfa: () => void;
  /** Recovery codes to show once after two-factor was turned on at sign-in. */
  recoveryCodes: string[] | null;
  acknowledgeRecoveryCodes: () => void;
  /** Choose the role to work as (multi-role accounts). */
  selectRole: (role: Role) => Promise<Me>;
  signOut: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  /** Re-reads `me` (after PIN, two-factor or preference changes). */
  reloadMe: () => Promise<Me | null>;
  /** Replaces `me` with a fresh copy returned by an API call. */
  updateMe: (me: Me) => void;
  /** Idle lock: locks the session on the server and hides the screen. */
  lock: () => Promise<void>;
  unlock: (pin: string) => Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** For components rendered only behind <RequireAuth> (signed in with an active role). */
export function useMe(): Me & { activeRole: NonNullable<Me['activeRole']> } {
  const { me } = useSession();
  if (!me?.activeRole)
    throw new Error('useMe used outside an authenticated route');
  return me as Me & { activeRole: NonNullable<Me['activeRole']> };
}
